import React, { useId, useRef, useState } from "react";
import { EditIcon, TrashIcon } from "@/components/icons";
import { NoteDisplay } from "@/components/NoteCard";
import { Button } from "@/components/ui/Button";
import { Drawer } from "@/components/ui/Drawer";
import { FooterConfirm, useFooterStep } from "@/components/ui/FooterConfirm";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { ENTITY_CONFIG } from "@/core/entityConfig";

/** What the drawer shows of a note; the panel's notes carry more. */
export interface NoteDrawerNote {
  id: string;
  content: string;
  tags: string[];
  createdAt: string;
}

interface NoteDrawerProps {
  /** The open note; null closes the drawer. */
  note: NoteDrawerNote | null;
  onClose: () => void;
  /** Saves the edit. Rejects on failure: the editor stays open with the draft and says so. */
  onSave: (noteId: string, content: string, tags: string[]) => Promise<void>;
  /** Deletes the note; on success the caller closes the drawer. Rejects on failure: the question stays and says so. */
  onDelete: (noteId: string) => Promise<void>;
}

const TAG_CHIP = "px-1.5 py-0.5 text-xs bg-blue-50 text-blue-700 rounded-sm";
const SAVE_FAILED = "Couldn't save the note. Try again.";
const DELETE_FAILED = "Couldn't delete the note. Try again.";

/** "print, , flyers " → ["print", "flyers"], as the add form splits them. */
function parseTags(input: string): string[] {
  return input
    .split(",")
    .map((tag) => tag.trim())
    .filter((tag) => tag);
}

/**
 * NoteDrawer: one Home note, every action (DD-051). It opens in view mode;
 * Edit turns the same drawer into the editor, with Save and Cancel in the
 * footer; Delete asks in the footer (DD-045), never in a dialog over the
 * drawer. The panel owns the calls (PUT and DELETE, as before).
 */
export function NoteDrawer({ note, onClose, onSave, onDelete }: NoteDrawerProps) {
  const [mode, setMode] = useState<"view" | "edit">("view");
  const [content, setContent] = useState("");
  const [tagsInput, setTagsInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<"delete" | null>(null);
  const footerStep = useFooterStep<"delete">(null, { step, go: setStep });
  const tagsId = useId();

  // Each note opens in view mode, with no question or error left from the
  // last one. Adjusted while rendering, so no frame shows the stale mode.
  const noteId = note?.id ?? null;
  const [shownId, setShownId] = useState<string | null>(noteId);
  if (shownId !== noteId) {
    setShownId(noteId);
    setMode("view");
    setStep(null);
    setError(null);
    setSaving(false);
    setDeleting(false);
  }
  // A request that settles after the drawer moved to another note must not
  // touch that note: each call compares the id it started with to this one.
  const currentId = useRef(noteId);
  currentId.current = noteId;

  const startEdit = () => {
    if (!note) return;
    setContent(note.content);
    setTagsInput(note.tags.join(", "));
    setError(null);
    setMode("edit");
  };

  const cancelEdit = () => {
    setError(null);
    setMode("view");
  };

  const save = async () => {
    if (!note || !content.trim()) return;
    const startedOn = note.id;
    setSaving(true);
    setError(null);
    try {
      await onSave(note.id, content, parseTags(tagsInput));
      if (currentId.current === startedOn) setMode("view");
    } catch (err) {
      console.error("Failed to update note:", err);
      if (currentId.current === startedOn) setError(SAVE_FAILED);
    } finally {
      if (currentId.current === startedOn) setSaving(false);
    }
  };

  const askDelete = () => {
    setError(null);
    footerStep.ask("delete");
  };

  const remove = async () => {
    if (!note) return;
    const startedOn = note.id;
    setDeleting(true);
    setError(null);
    try {
      await onDelete(note.id);
    } catch (err) {
      console.error("Failed to delete note:", err);
      if (currentId.current === startedOn) setError(DELETE_FAILED);
    } finally {
      if (currentId.current === startedOn) setDeleting(false);
    }
  };

  let body: React.ReactNode = null;
  let footer: React.ReactNode = null;

  if (note && mode === "view") {
    body = (
      <div className="space-y-3">
        <NoteDisplay content={note.content} />
        {note.tags.length > 0 && (
          <div className="flex gap-1 flex-wrap">
            {note.tags.map((tag) => (
              <span key={tag} className={TAG_CHIP}>
                {tag}
              </span>
            ))}
          </div>
        )}
        <p className="text-xs text-gray-400">{new Date(note.createdAt).toLocaleString()}</p>
      </div>
    );
    footer =
      footerStep.step === "delete" ? (
        <FooterConfirm
          confirmLabel="Delete"
          onConfirm={() => void remove()}
          onKeep={footerStep.leave}
          pending={deleting}
          loading={deleting}
          stacked
        >
          Delete this note? This can't be undone.
          {error && <span className="block text-xs text-red-600">{error}</span>}
        </FooterConfirm>
      ) : (
        <>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={askDelete}
            autoFocus={footerStep.returnsTo("delete")}
            className="mr-auto text-red-600"
          >
            <TrashIcon className="w-4 h-4 mr-1.5" />
            Delete
          </Button>
          <Button type="button" variant="secondary" size="sm" onClick={startEdit}>
            <EditIcon className="w-4 h-4 mr-1.5" />
            Edit
          </Button>
        </>
      );
  } else if (note && mode === "edit") {
    body = (
      <div className="space-y-4">
        <div>
          <span className="block text-sm font-medium text-gray-700 mb-1">Content</span>
          {/* Reads the note once on mount: each trip into edit mode mounts it fresh. */}
          <RichTextEditor
            key={note.id}
            format="markdown"
            initialContent={note.content}
            onContentChange={setContent}
            autoFocus
            className="min-h-[8rem] rounded-lg border border-gray-300 py-2"
          />
        </div>
        <div>
          <label htmlFor={tagsId} className="block text-sm font-medium text-gray-700 mb-1">
            Tags (comma-separated)
          </label>
          <input
            id={tagsId}
            type="text"
            value={tagsInput}
            onChange={(e) => setTagsInput(e.target.value)}
            placeholder="e.g. content, strategy, branding"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>
    );
    footer = (
      <>
        {error && <p role="alert" className="mr-auto text-xs text-red-600">{error}</p>}
        <Button type="button" variant="secondary" size="sm" onClick={cancelEdit} disabled={saving}>
          Cancel
        </Button>
        <Button type="button" size="sm" onClick={() => void save()} disabled={!content.trim()} isLoading={saving}>
          Save
        </Button>
      </>
    );
  }

  return (
    <Drawer
      isOpen={note !== null}
      onClose={onClose}
      title={mode === "edit" ? `Edit ${ENTITY_CONFIG.note.label}` : ENTITY_CONFIG.note.label}
      footer={footer}
      bodyPadding
      a11yDescription="The note, its tags and when it was written."
      className="md:max-w-lg"
    >
      {body}
    </Drawer>
  );
}
